import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import { api, ApiError, type Kind } from "../../api";
import { AnswerText, ErrorNote, formatDate, HealthBadge, KIND_LABEL, Loading, PriorityChip, STATUS_LABEL, timeAgo, useApi } from "../../components/ui";

interface Health {
  orgId: string;
  name: string;
  tier: string;
  accountOwner: string | null;
  open: number;
  urgent: number;
  high: number;
  untriaged: number;
  stale: number;
  oldestOpenDays: number;
  opened30d: number;
  resolved30d: number;
  activeProjects: number;
  blockedProjects: number;
  lastActivityAt: string | null;
  score: number;
  band: "healthy" | "watch" | "at_risk";
  reasons: string[];
}

export interface ProjectSummary {
  id: string;
  name: string;
  orgId: string | null;
  orgName: string | null;
  status: string;
  ownerName: string | null;
  targetDate: string | null;
  overdue: boolean;
  total: number;
  done: number;
  openUrgentHigh: number;
}

interface Dashboard {
  counts: Record<string, number | null>;
  byPriority: Record<string, number>;
  byKind: Record<string, number>;
  trend: Array<{ week: string; opened: number; resolved: number }>;
  topQueue: Array<{ id: string; number: number; kind: Kind; title: string; status: keyof typeof STATUS_LABEL; priority: number | null; effort: number | null; org_name: string | null; score: number; assignee: string | null }>;
  githubRepos: Array<{ repo: string; open: number }>;
  health: { bands: Record<"healthy" | "watch" | "at_risk", number>; customers: Health[] };
  projects: ProjectSummary[];
}

export function StaffDashboard() {
  const { data, error, loading } = useApi<Dashboard>("/staff/dashboard");
  return (
    <div className="page stack">
      <AskBox />
      <ErrorNote message={error} />
      {loading && !data ? <Loading /> : data && <DashboardBody d={data} />}
    </div>
  );
}

const SUGGESTIONS = ["What should I work on next?", "What is the highest priority issue?", "What's a quick feature I can build or modify?", "Which customers need attention this week?"];

function AskBox() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (question: string) => {
    if (question.trim().length < 3) return;
    setQ(question);
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      const r = await api<{ answer: string }>("/staff/ask", { method: "POST", json: { question } });
      setAnswer(r.answer);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The assistant couldn't answer just now.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="ask" aria-labelledby="ask-title" data-tour="ask-box">
      <p className="eyebrow" id="ask-title">Ask the queue</p>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          run(q);
        }}
      >
        <label htmlFor="ask-q" className="sr-only">
          Ask a question about the work queue
        </label>
        <input id="ask-q" type="text" placeholder="e.g. What should I work on next?" value={q} maxLength={1000} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-primary" disabled={busy || q.trim().length < 3}>
          {busy ? "Thinking…" : "Ask"}
        </button>
      </form>
      {!answer && !busy && (
        <div className="ask-suggestions" data-tour="ask-suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" onClick={() => run(s)}>
              {s}
            </button>
          ))}
        </div>
      )}
      {busy && (
        <p className="small" role="status" style={{ color: "var(--muted-dark)", marginBottom: 0 }}>
          Reading the queue, customer health and projects…
        </p>
      )}
      {error && (
        <p className="small" role="alert" style={{ color: "#ffb4a8", marginBottom: 0 }}>
          {error}
        </p>
      )}
      {answer && (
        <div className="ask-answer" aria-live="polite">
          <AnswerText text={answer} base="/staff/items" />
        </div>
      )}
    </section>
  );
}

function DashboardBody({ d }: { d: Dashboard }) {
  const c = d.counts;
  const n = (k: string) => c[k] ?? 0;
  return (
    <>
      <div className="tiles">
        <Tile label="Open work" value={n("open")} sub={`${n("in_progress")} in progress · ${n("waiting")} waiting`} href="/staff/queue" />
        <Tile label="Unsorted" value={n("unsorted")} sub="Inbound, no customer yet" href="/staff/queue?view=unsorted" />
        <Tile label="Needs triage" value={n("untriaged")} sub="No priority yet" href="/staff/queue?view=triage" />
        <Tile label="Urgent + high" value={(d.byPriority["0"] ?? 0) + (d.byPriority["1"] ?? 0)} sub={`${d.byPriority["0"] ?? 0} urgent`} href="/staff/queue?priority=0" />
        <Tile label="Unassigned" value={n("unassigned")} sub="Excludes GitHub issues" href="/staff/queue?assignee=none" />
        <Tile label="Overdue" value={n("overdue")} sub="Past due date" />
        <Tile label="This week" value={`+${n("opened_7d")} / −${n("resolved_7d")}`} sub="Opened / resolved" />
        <Tile label="Avg. resolution" value={c.avg_resolve_days_30d === null ? "—" : `${c.avg_resolve_days_30d}d`} sub="Tickets + feedback, 30 days" />
        <Tile label="Customers at risk" value={d.health.bands.at_risk} sub={`${d.health.bands.watch} on watch · ${d.health.bands.healthy} healthy`} href="/staff/customers" />
      </div>

      <div className="grid grid-2">
        <section className="card">
          <div className="card-head">
            <h2>Top of the queue</h2>
            <Link href="/staff/queue">Full queue</Link>
          </div>
          {d.topQueue.length === 0 ? (
            <p className="muted">Nothing open. Enjoy it.</p>
          ) : (
            <ol className="list-plain">
              {d.topQueue.map((i) => (
                <li key={i.id} className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link href={`/staff/items/${i.number}`} style={{ fontWeight: 600, color: "var(--text)" }}>
                      {i.title}
                    </Link>
                    <div className="small muted">
                      <span className="mono">DL-{i.number}</span> · {KIND_LABEL[i.kind]}
                      {i.org_name ? ` · ${i.org_name}` : ""}
                      {i.assignee ? ` · ${i.assignee}` : " · unassigned"}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 6 }}>
                    <PriorityChip priority={i.priority} />
                    <span className="mono small muted" title="Queue score">
                      {i.score}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="card stack">
          <div className="card-head" style={{ marginBottom: 0 }}>
            <h2>Opened vs resolved</h2>
            <span className="small muted">Last 8 weeks</span>
          </div>
          <TrendChart data={d.trend} />
          <div>
            <h3>Open by priority</h3>
            <PriorityBars byPriority={d.byPriority} />
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <h2>Customer health</h2>
          <Link href="/staff/customers">All customers</Link>
        </div>
        <HealthTable customers={d.health.customers.slice(0, 12)} />
        {d.health.customers.length > 12 && <p className="small muted">Showing the 12 that need the most attention.</p>}
      </section>

      <div className="grid grid-2">
        <section className="card">
          <div className="card-head">
            <h2>In-flight projects</h2>
            <Link href="/staff/projects">All projects</Link>
          </div>
          <ProjectList projects={d.projects} />
        </section>
        <section className="card">
          <div className="card-head">
            <h2>Development backlog</h2>
            <span className="small muted">Open GitHub issues</span>
          </div>
          {d.githubRepos.length === 0 ? (
            <p className="muted">
              No GitHub issues synced yet. Connect the GitHub App in <Link href="/staff/settings">Settings</Link>.
            </p>
          ) : (
            <div>
              {d.githubRepos.map((r) => {
                const max = d.githubRepos[0]!.open;
                return (
                  <div className="bar-row" key={r.repo}>
                    <Link href={`/staff/queue?kind=github_issue&repo=${encodeURIComponent(r.repo)}`} className="small" title={r.repo} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.repo.split("/")[1]}
                    </Link>
                    <div className="bar-track" aria-hidden="true">
                      <span style={{ width: `${(r.open / max) * 100}%` }} />
                    </div>
                    <span className="mono small num">{r.open}</span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="small muted" style={{ marginBottom: 0 }}>
            Open items by kind:{" "}
            {Object.entries(d.byKind)
              .map(([k, v]) => `${KIND_LABEL[k as Kind]} ${v}`)
              .join(" · ") || "none"}
          </p>
        </section>
      </div>
    </>
  );
}

function Tile({ label, value, sub, href }: { label: string; value: number | string; sub?: string; href?: string }) {
  const inner = (
    <>
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      {sub && <div className="tile-sub">{sub}</div>}
    </>
  );
  return href ? (
    <Link href={href} className="tile">
      {inner}
    </Link>
  ) : (
    <div className="tile">{inner}</div>
  );
}

function PriorityBars({ byPriority }: { byPriority: Record<string, number> }) {
  const rows = [
    ["0", "Urgent"],
    ["1", "High"],
    ["untriaged", "Untriaged"],
    ["2", "Normal"],
    ["3", "Low"],
  ] as const;
  const max = Math.max(1, ...rows.map(([k]) => byPriority[k] ?? 0));
  return (
    <div style={{ marginTop: 8 }}>
      {rows.map(([k, label]) => (
        <div className="bar-row" key={k}>
          <span className="small">{label}</span>
          <div className="bar-track" aria-hidden="true">
            <span style={{ width: `${((byPriority[k] ?? 0) / max) * 100}%` }} />
          </div>
          <span className="mono small num">{byPriority[k] ?? 0}</span>
        </div>
      ))}
    </div>
  );
}

/** Grouped bars, one axis, legend + hover tooltip + table view. */
function TrendChart({ data }: { data: Dashboard["trend"] }) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const W = 560;
  const H = 180;
  const pad = { l: 28, r: 8, t: 8, b: 24 };
  const max = Math.max(4, ...data.flatMap((d) => [d.opened, d.resolved]));
  const ticks = [0, Math.round(max / 2), max];
  const band = (W - pad.l - pad.r) / data.length;
  const bw = Math.min(16, (band - 10) / 2);
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const label = (w: string) => new Date(`${w}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return (
    <div className="chart">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div className="chart-legend">
          <span>
            <i className="swatch" style={{ background: "var(--series-1)" }} /> Opened
          </span>
          <span>
            <i className="swatch" style={{ background: "var(--series-2)" }} /> Resolved
          </span>
        </div>
        <button className="btn-link small" onClick={() => setTable((t) => !t)}>
          {table ? "Show chart" : "Show table"}
        </button>
      </div>
      {table ? (
        <table>
          <thead>
            <tr>
              <th>Week of</th>
              <th className="num">Opened</th>
              <th className="num">Resolved</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.week}>
                <td>{label(d.week)}</td>
                <td className="num">{d.opened}</td>
                <td className="num">{d.resolved}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Items opened and resolved per week, last 8 weeks" onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeWidth="1" />
              <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="var(--text-muted)">
                {t}
              </text>
            </g>
          ))}
          {data.map((d, i) => {
            const x0 = pad.l + band * i + band / 2 - bw - 1;
            return (
              <g key={d.week} onMouseEnter={() => setHover(i)}>
                <rect x={pad.l + band * i} y={pad.t} width={band} height={H - pad.t - pad.b} fill={hover === i ? "var(--surface-2)" : "transparent"} />
                <Bar x={x0} w={bw} y={y(d.opened)} base={y(0)} fill="var(--series-1)" />
                <Bar x={x0 + bw + 2} w={bw} y={y(d.resolved)} base={y(0)} fill="var(--series-2)" />
                {(i % 2 === 1 || data.length <= 6) && (
                  <text x={pad.l + band * i + band / 2} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--text-muted)">
                    {label(d.week)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}
      {!table && hover !== null && data[hover] && (
        <div className="chart-tip" style={{ left: `${((pad.l + band * hover + band / 2) / W) * 100}%`, top: 24 }}>
          Week of {label(data[hover]!.week)}: {data[hover]!.opened} opened · {data[hover]!.resolved} resolved
        </div>
      )}
    </div>
  );
}

/** Bar anchored to the baseline with a 4px rounded top. */
function Bar({ x, w, y, base, fill }: { x: number; w: number; y: number; base: number; fill: string }) {
  const h = base - y;
  if (h <= 0) return null;
  const r = Math.min(4, h, w / 2);
  return <path d={`M${x},${base} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${base} Z`} fill={fill} />;
}

export function HealthTable({ customers }: { customers: Health[] }) {
  if (customers.length === 0) return <p className="muted">No customers yet. Add one under Customers.</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Health</th>
            <th className="num">Open</th>
            <th className="num">Urgent/high</th>
            <th className="num">Stale</th>
            <th className="num">Oldest</th>
            <th className="num">30d in/out</th>
            <th>Why</th>
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((h) => (
            <tr key={h.orgId}>
              <td className="title-cell">
                <Link href={`/staff/customers/${h.orgId}`}>{h.name}</Link>
                <div className="small muted">
                  {h.tier === "priority" ? "Priority tier" : "Standard"}
                  {h.accountOwner ? ` · ${h.accountOwner}` : ""}
                </div>
              </td>
              <td>
                <HealthBadge band={h.band} score={h.score} />
              </td>
              <td className="num">{h.open}</td>
              <td className="num">
                {h.urgent}/{h.high}
              </td>
              <td className="num">{h.stale}</td>
              <td className="num">{h.oldestOpenDays ? `${h.oldestOpenDays}d` : "—"}</td>
              <td className="num">
                {h.opened30d}/{h.resolved30d}
              </td>
              <td className="small muted" style={{ minWidth: 220 }} title={h.reasons.join("; ")}>
                {h.reasons.length ? h.reasons.slice(0, 2).join("; ") + (h.reasons.length > 2 ? ` +${h.reasons.length - 2} more` : "") : "Nothing outstanding"}
              </td>
              <td className="small muted">{timeAgo(h.lastActivityAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PROJECT_STATUS: Record<string, string> = { planned: "Planned", active: "Active", blocked: "Blocked", done: "Done", cancelled: "Cancelled" };
const PROJECT_CHIP: Record<string, string> = { planned: "chip-plain", active: "chip-in_progress", blocked: "chip-open", done: "chip-resolved", cancelled: "chip-closed" };

export function ProjectList({ projects }: { projects: ProjectSummary[] }) {
  if (projects.length === 0) return <p className="muted">No projects in flight.</p>;
  return (
    <ul className="list-plain">
      {projects.map((p) => {
        const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
        return (
          <li key={p.id} className="stack" style={{ gap: 6 }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div>
                <Link href={`/staff/queue?project=${p.id}&status=any`} style={{ fontWeight: 600, color: "var(--text)" }}>
                  {p.name}
                </Link>
                <div className="small muted">
                  {p.orgName ?? "Internal"}
                  {p.ownerName ? ` · ${p.ownerName}` : ""}
                  {p.targetDate ? ` · target ${formatDate(p.targetDate)}` : ""}
                  {p.overdue && <strong style={{ color: "var(--danger)" }}> · overdue</strong>}
                </div>
              </div>
              <span className={`chip ${PROJECT_CHIP[p.status]}`}>{PROJECT_STATUS[p.status]}</span>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="progress" style={{ flex: 1 }} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${p.name} progress`}>
                <span style={{ width: `${pct}%` }} />
              </div>
              <span className="small muted mono">
                {p.done}/{p.total}
              </span>
              {p.openUrgentHigh > 0 && <span className="small" style={{ color: "var(--danger)", fontWeight: 600 }}>{p.openUrgentHigh} urgent/high</span>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
